import { UTCDate } from '@date-fns/utc';
import { formatDistanceStrict, isValid } from 'date-fns';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';

import { getOffsetLabel } from './getOffsetLabel';

export type FormatDateParam = {
  date: string;
  dateFormat?: string;
  displayLocalTime?: boolean;
  showOffset?: boolean;
};

export const isDateWithTimeZone = /[+-](\d{2}):(\d{2})|Z$/;

/** Parses wall-time strings via `fromZonedTime`; ISO with Z/offset is always an absolute instant. */
export const handleDateParsing = (dateTime: string, timeZone: string): Date => {
  if (isDateWithTimeZone.test(dateTime)) {
    return new UTCDate(dateTime);
  }

  return fromZonedTime(dateTime, timeZone);
};

export const formatDate = (param: FormatDateParam) => {
  const { date, dateFormat = 'yyyy-MM-dd HH:mm:ss', displayLocalTime, showOffset } = param;

  const parsedDate = handleDateParsing(date, 'UTC');

  if (!isValid(parsedDate)) {
    return { dateLabel: 'Invalid date', fromNow: '' };
  }

  const localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const timeZone = displayLocalTime ? localTimeZone : 'UTC';
  const formattedDate = formatInTimeZone(parsedDate, timeZone, dateFormat);

  const fromNow = formatDistanceStrict(parsedDate, new Date(), { addSuffix: true });
  const dateLabel = showOffset
    ? `${formattedDate} (${displayLocalTime ? getOffsetLabel(formattedDate) : 'UTC'})`
    : formattedDate;

  return { dateLabel, fromNow };
};
